import * as THREE from 'three';
import type { Course } from '../course/Course';
import type { HeightField } from '../terrain/HeightField';
import type { SplatMapBaker } from './SplatMapBaker';
import { computeRegionBounds } from '../utils/geometry';
import type { Terrain } from '../terrain/Terrain';
import type { MaterialLibrary } from './MaterialLibrary';

/**
 * Bunkers are recessed relative to surrounding terrain — this renderer
 * paints each bunker's sand weight into the shared splat map and carves a
 * shallow bowl into the HeightField, both sourced directly from each
 * Hole's own bunker list (never inventing bunker shapes/positions itself).
 */
export class BunkerRenderer {
  private readonly overlayMeshes: THREE.Mesh[] = [];
  private static readonly RECESS_DEPTH = 0.85;
  private static readonly RECESS_FEATHER = 9;

  paintSplat(course: Course, baker: SplatMapBaker): void {
    for (const hole of course.holes) {
      for (const bunker of hole.def.bunkers) {
        baker.paintRegion(bunker, 'b');
      }
    }
  }

  sculpt(course: Course, heightField: HeightField): void {
    for (const hole of course.holes) {
      for (const bunker of hole.def.bunkers) {
        const rimHeight = heightField.averageHeightInRegion(bunker);
        heightField.flattenRegionToward(
          bunker,
          rimHeight - BunkerRenderer.RECESS_DEPTH,
          BunkerRenderer.RECESS_FEATHER,
        );
      }
    }
  }

  /**
   * Adds a thin sand overlay for play-test visibility. The terrain splat map
   * remains the gameplay/source-of-truth material, but this explicit overlay
   * guarantees newly-authored bunkers are visible immediately even when the
   * terrain texture was created before the player entered play-test.
   */
  renderOverlays(
    course: Course,
    terrain: Terrain,
    materials: MaterialLibrary,
    parent: THREE.Object3D,
  ): void {
    for (const hole of course.holes) {
      for (const bunker of hole.def.bunkers) {
        const geometry =
          bunker.kind === 'circle'
            ? new THREE.CircleGeometry(bunker.radius, 64)
            : new THREE.ShapeGeometry(
                new THREE.Shape(bunker.points.map((p) => new THREE.Vector2(p.x, p.z))),
              );
        const mesh = new THREE.Mesh(geometry, materials.surface('sand').clone());
        mesh.rotation.x = bunker.kind === 'circle' ? -Math.PI / 2 : Math.PI / 2;
        if (bunker.kind === 'circle') {
          mesh.position.set(
            bunker.center.x,
            terrain.getHeightAt(bunker.center.x, bunker.center.z) + 0.035,
            bunker.center.z,
          );
        } else {
          const bounds = computeRegionBounds(bunker);
          mesh.position.y = terrain.getHeightAt(bounds.centerX, bounds.centerZ) + 0.035;
        }
        mesh.name = `BunkerOverlay:${hole.number}`;
        mesh.receiveShadow = true;
        parent.add(mesh);
        this.overlayMeshes.push(mesh);
      }
    }
  }

  dispose(): void {
    for (const mesh of this.overlayMeshes) {
      mesh.parent?.remove(mesh);
      mesh.geometry.dispose();
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      materials.forEach((material) => material.dispose());
    }
    this.overlayMeshes.length = 0;
  }
}
